import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const GAP = "var(--space-2)";
/**
 * The narrow values, declared on the `data-layout="table"` element the width query targets.
 * Each track but an `end` one may shrink below its width (`minmax(0, …)`), so where the fixed
 * tracks are wider than the sheet (below about 770px) they give way rather than run past it,
 * and the `end` column (the row's ⋯) keeps its full width (issue 214, 212-ux P2-1).
 */
function narrowStyle(columns) {
    const narrow = columns
        .slice(1)
        .map((c) => (c.end || c.width.startsWith("minmax(") ? c.width : `minmax(0, ${c.width})`))
        .join(" ") || "minmax(0, 1fr)";
    return { "--table-cols-narrow": narrow, "--table-lead-col-narrow": "1 / -1" };
}
function trackStyle(columns) {
    const wide = columns.map((c) => c.width).join(" ");
    return {
        display: "grid",
        gridTemplateColumns: `var(--table-cols, ${wide})`,
        columnGap: GAP,
        alignItems: "center",
    };
}
function Bars({ widths }) {
    return (_jsx("span", { "data-table-skeleton": "", style: { display: "flex", flexDirection: "column", gap: "var(--space-1)" }, children: widths.map((width, index) => (_jsx("span", { style: {
                display: "block", width, maxWidth: "100%", height: index === 0 ? 12 : 10,
                borderRadius: "var(--radius-inline)", background: "var(--tone)",
            } }, index))) }));
}
export function Table({ columns, rows = [], loading = false, skeletonRows = 6, ...rest }) {
    const tracks = trackStyle(columns);
    const lead = (index) => index === 0 ? { gridColumn: "var(--table-lead-col, auto)", minWidth: 0 } : { minWidth: 0 };
    const cellAlign = (column) => column.end ? { display: "flex", justifyContent: "flex-end" } : {};
    const bodyRows = loading
        ? Array.from({ length: skeletonRows }, (_, row) => ({
            key: `skeleton-${row}`,
            data: { "data-table-row": "skeleton" },
            cells: columns.map((column) => {
                const widths = column.skeleton?.[row % column.skeleton.length];
                return widths === undefined ? null : _jsx(Bars, { widths: widths });
            }),
        }))
        : rows.map((row) => ({
            key: row.key, data: row.data, tinted: row.tinted, cells: columns.map((c) => row.cells[c.key]),
        }));
    return (_jsxs("div", { role: "table", "data-layout": "table", "aria-busy": loading || undefined, ...rest, style: {
            ...narrowStyle(columns),
            fontSize: "var(--type-body-size)",
            lineHeight: "var(--type-row-title-line)",
            color: "var(--ink-1)",
        }, children: [_jsx("div", { role: "rowgroup", children: _jsx("div", { role: "row", "data-table-header": "", style: { ...tracks, padding: "0 0 var(--space-1)" }, children: columns.map((column, index) => (_jsx("div", { role: "columnheader", "data-column": column.key, "aria-label": column.ariaLabel, style: {
                            ...lead(index),
                            fontSize: "var(--type-small-size)", lineHeight: "var(--type-small-line)",
                            fontWeight: "var(--type-small-weight-strong)", color: "var(--ink-2)",
                        }, children: column.label }, column.key))) }) }), _jsx("div", { role: "rowgroup", children: bodyRows.map((row, rowIndex) => (_jsx("div", { role: "row", ...row.data, "data-table-row-tinted": row.tinted ? "" : undefined, style: {
                        ...tracks,
                        minHeight: "var(--table-row-min-h)", boxSizing: "border-box",
                        ...(row.tinted
                            ? {
                                background: "var(--tone)", margin: "0 calc(var(--space-15) * -1)",
                                padding: "var(--space-1) var(--space-15)",
                            }
                            : { padding: "var(--space-1) 0" }),
                        borderTop: "var(--border-hairline) solid var(--line)",
                        borderBottom: rowIndex === bodyRows.length - 1 ? "var(--border-hairline) solid var(--line)" : undefined,
                    }, children: row.cells.map((cell, index) => (_jsx("div", { role: "cell", "data-column": columns[index].key, style: { ...lead(index), ...cellAlign(columns[index]) }, children: cell }, columns[index].key))) }, row.key))) })] }));
}
